import type {
  ConditionKind,
  GroupOperator,
  NodePath,
  TreeConditionNode,
  TreeGroupNode,
  TreeNode,
} from '@/components/tree-dnd/tree-dnd-types';
import {
  canAcceptChildren,
  isGroupNode,
} from '@/components/tree-dnd/tree-dnd-types';
import {
  isInvalidMove,
  isNoOpMove,
  pathsEqual,
  remapPathAfterRemoval,
} from '@/components/tree-dnd/tree-dnd-path';
import { createDefaultConditionNode } from '@/components/tree-dnd/tree-dnd-conditions/condition-defaults';

function cloneGroupNode(node: TreeGroupNode): TreeGroupNode {
  return {
    ...node,
    children: node.children.map(cloneNode),
  };
}

function cloneNode(node: TreeNode): TreeNode {
  if (isGroupNode(node)) {
    return cloneGroupNode(node);
  }
  return { ...node };
}

function getChild(node: TreeGroupNode, index: number): TreeNode | null {
  return node.children[index] ?? null;
}

function setChild(
  node: TreeGroupNode,
  index: number,
  child: TreeNode
): TreeGroupNode {
  const children = [...node.children];
  children[index] = child;
  return { ...node, children };
}

export function getNodeAtPath(
  root: TreeGroupNode,
  path: NodePath
): TreeNode | null {
  let current: TreeNode = root;
  for (const index of path) {
    if (!isGroupNode(current)) return null;
    const next = getChild(current, index);
    if (!next) return null;
    current = next;
  }
  return current;
}

function updateGroupAtPath(
  root: TreeGroupNode,
  path: NodePath,
  updater: (node: TreeGroupNode) => TreeGroupNode
): TreeGroupNode {
  if (path.length === 0) {
    return updater(root);
  }

  const [head, ...tail] = path;
  const child = getChild(root, head);
  if (!child || !isGroupNode(child)) return root;

  return setChild(root, head, updateGroupAtPath(child, tail, updater));
}

export function removeNodeAtPath(
  root: TreeGroupNode,
  path: NodePath
): TreeGroupNode | null {
  if (path.length === 0) {
    return null;
  }

  if (path.length === 1) {
    const index = path[0];
    const children = root.children.filter((_, i) => i !== index);
    return { ...root, children };
  }

  const parentPath = path.slice(0, -1);
  const index = path[path.length - 1];
  const parent = getNodeAtPath(root, parentPath);
  if (!parent || !isGroupNode(parent)) return root;

  const children = parent.children.filter((_, i) => i !== index);
  return updateGroupAtPath(cloneGroupNode(root), parentPath, (node) => ({
    ...node,
    children,
  }));
}

export function insertChild(
  root: TreeGroupNode,
  parentPath: NodePath,
  child: TreeNode,
  index?: number
): TreeGroupNode {
  return updateGroupAtPath(cloneGroupNode(root), parentPath, (node) => {
    const children = [...node.children];
    const insertAt = index ?? children.length;
    children.splice(insertAt, 0, child);
    return { ...node, children };
  });
}

function updateNodeAtPath(
  root: TreeGroupNode,
  path: NodePath,
  updater: (node: TreeNode) => TreeNode
): TreeGroupNode {
  if (path.length === 0) {
    const updated = updater(root);
    return isGroupNode(updated) ? updated : root;
  }

  const [head, ...tail] = path;
  const child = getChild(root, head);
  if (!child) return root;

  if (tail.length === 0) {
    return setChild(root, head, updater(child));
  }

  if (!isGroupNode(child)) return root;
  return setChild(root, head, updateNodeAtPath(child, tail, updater));
}

export function updateConditionAtPath(
  root: TreeGroupNode,
  path: NodePath,
  patch: Partial<Pick<TreeConditionNode, 'negated' | 'args'>>
): TreeGroupNode {
  return updateNodeAtPath(cloneGroupNode(root), path, (node) => {
    if (node.type !== 'condition') return node;
    return { ...node, ...patch } as TreeConditionNode;
  });
}

export function updateGroupOperatorAtPath(
  root: TreeGroupNode,
  path: NodePath,
  operator: GroupOperator
): TreeGroupNode {
  return updateNodeAtPath(cloneGroupNode(root), path, (node) => {
    if (node.type !== 'group') return node;
    return { ...node, operator };
  });
}

export function setNegatedAtPath(
  root: TreeGroupNode,
  path: NodePath,
  negated: boolean
): TreeGroupNode {
  return updateNodeAtPath(cloneGroupNode(root), path, (node) => ({
    ...node,
    negated,
  }));
}

export function createConditionNode(kind: ConditionKind): TreeConditionNode {
  return createDefaultConditionNode(kind);
}

export function createGroupNode(
  operator: GroupOperator = 'AND'
): TreeGroupNode {
  return {
    id: crypto.randomUUID(),
    type: 'group',
    operator,
    negated: false,
    children: [],
  };
}

export function appendChild(
  root: TreeGroupNode,
  parentPath: NodePath,
  child: TreeNode
): TreeGroupNode {
  return insertChild(root, parentPath, child);
}

export function moveNode(
  root: TreeGroupNode,
  sourcePath: NodePath,
  destinationParentPath: NodePath,
  destinationIndex: number
): TreeGroupNode | null {
  if (isInvalidMove(sourcePath, destinationParentPath)) {
    return null;
  }

  const draggedNode = getNodeAtPath(root, sourcePath);
  if (!draggedNode) return null;

  const destinationParent = getNodeAtPath(root, destinationParentPath);
  if (!canAcceptChildren(destinationParent)) {
    return null;
  }

  let insertIndex = Math.max(
    0,
    Math.min(destinationIndex, destinationParent.children.length)
  );

  const sourceParentPath = sourcePath.slice(0, -1);
  const sourceIndex = sourcePath[sourcePath.length - 1];
  if (
    pathsEqual(sourceParentPath, destinationParentPath) &&
    sourceIndex < insertIndex
  ) {
    insertIndex -= 1;
  }

  if (isNoOpMove(sourcePath, destinationParentPath, destinationIndex)) {
    return null;
  }

  const treeWithoutNode = removeNodeAtPath(root, sourcePath);
  if (!treeWithoutNode) {
    return null;
  }

  const remappedParentPath = remapPathAfterRemoval(
    sourcePath,
    destinationParentPath
  );

  return insertChild(
    treeWithoutNode,
    remappedParentPath,
    draggedNode,
    insertIndex
  );
}

export interface SiblingMove {
  sourcePath: NodePath;
  destinationParentPath: NodePath;
  destinationIndex: number;
}

/**
 * Translates "move up/down among siblings" into `moveNode` arguments.
 * Moving down targets `index + 2` because `moveNode` discounts the removed
 * node when it sits before the insertion point in the same parent.
 */
export function getSiblingMove(
  path: NodePath,
  siblingCount: number,
  direction: 'up' | 'down'
): SiblingMove | null {
  if (path.length === 0) return null;

  const index = path[path.length - 1];
  const destinationParentPath = path.slice(0, -1);

  if (direction === 'up') {
    return index > 0
      ? { sourcePath: path, destinationParentPath, destinationIndex: index - 1 }
      : null;
  }
  return index < siblingCount - 1
    ? { sourcePath: path, destinationParentPath, destinationIndex: index + 2 }
    : null;
}

export const INITIAL_TREE: TreeGroupNode = {
  id: 'root',
  type: 'group',
  operator: 'AND',
  negated: false,
  children: [],
};

/** Strip root metadata for a cleaner JSON preview. */
export function treeToSerializable(root: TreeGroupNode): TreeNode[] {
  return root.children;
}

/** Whether the tree contains at least one condition node. */
export function treeHasConditions(root: TreeGroupNode): boolean {
  for (const child of root.children) {
    if (child.type === 'condition') return true;
    if (treeHasConditions(child)) return true;
  }
  return false;
}

/** Whether any root child is a nested group (beyond the always-present root). */
export function treeHasNestedGroups(root: TreeGroupNode): boolean {
  return root.children.some((child) => child.type === 'group');
}
